/**
 * Auth store.
 *
 */

import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators';
import type { AuthModel } from '@/types/models/auth';
import UserAuthModel from '@/lib/models/userAuth';
import InputEntity from '@/lib/formFactory/inputEntity';
import { login as loginApi, profile } from '@/api/user';

import store from '@/store';
import siteModule from '@/store/site';
import userModule from '@/store/user';
import VoxAuthModule from '@/store/voxAuth';

export const MODULE_NAME = 'auth';

@Module({ dynamic: true, store, name: MODULE_NAME, namespaced: true })
class AuthModule extends VuexModule {
    text: Record<string, any> = {
        btnLogin: 'Войти',
    };
    model: AuthModel;
    userAuth = new UserAuthModel();

    constructor(module: AuthModule) {
        super(module);
        const loginEntity = new InputEntity('login');
        loginEntity.setTitle('Логин');
        loginEntity.setValidation({
            empty: 'Поле не заполнено',
        });

        const passwordEntity = new InputEntity('password');
        passwordEntity.setType('password');
        passwordEntity.setTitle('Пароль');
        loginEntity.setValidation({
            empty: 'Поле не заполнено',
        });

        this.model = {
            login: loginEntity.entity[loginEntity.entityName],
            password: passwordEntity.entity[passwordEntity.entityName],
        };
    }

    @Mutation
    setModel() {
        this.userAuth.login = 'test';
        this.userAuth.password = '';
    }

    @Mutation
    setLoginValue(value: string) {
        this.model.login.value = value;
    }

    @Mutation
    setPasswordValue(value: string) {
        this.model.password.value = value;
    }

    @Mutation
    SET_ERROR(params: { field: 'login' | 'password'; error: { class: string; message: string } }) {
        const { field, error } = params;
        this.model[field].error.class = error.class;
        this.model[field].error.message = error.message;
    }

    @Action({ rawError: true })
    async setValidateField(field: 'login' | 'password') {
        if (!this.model[field].value) {
            await this.SET_ERROR({
                field,
                error: { class: 'cp-error', message: this.model[field].validation.empty },
            });

            return false;
        }

        await this.SET_ERROR({
            field,
            error: { class: '', message: '' },
        });

        return true;
    }

    @Action({ rawError: true })
    async validate() {
        let isError = false;

        const isValidLogin = await this.setValidateField('login');
        if (!isValidLogin) {
            isError = true;
        }

        const isValidPassword = await this.setValidateField('password');
        if (!isValidPassword) {
            isError = true;
        }

        return isError;
    }

    @Action({ rawError: true })
    async profile () {
        try {
            const token = localStorage.getItem('tokenAdmin') || null;
            userModule.setToken(token);

            if (token) {
                const { user, doctor } = await profile();

                if (user) {
                    userModule.setUser(user);

                    if (doctor) {
                        userModule.setDoctorId(doctor.id);  
                    }
                } else {
                    userModule.removeToken();
                }
            }
        } catch (_) {
            userModule.clearUser();
            userModule.removeToken();
            userModule.removeToken();
        }
    }

    @Action({ rawError: true })
    async loginRequest(params: {login: string; password: string}) {
        try {
            const result = await loginApi(params.login, params.password);

            const user = result.result.user;
            await userModule.setUser(user);
            await userModule.setToken(result.token);
            await siteModule.init();

            // Инициализируем VoxAuth для докторов после авторизации
            if (userModule.isDoctor) {
                try {
                    await VoxAuthModule.makeAuth();
                } catch (error) {
                    console.error('Failed to initialize VoxAuth after login:', error);
                }
            }
        } catch (error: any) {
            const message = error.response?.data?.message || 'error';

            await this.SET_ERROR({
                field: 'login',
                error: { class: 'cp-error', message: '' },
            });
            await this.SET_ERROR({
                field: 'password',
                error: { class: 'cp-error', message: message },
            });
        }
    }

    @Action
    async logoutRequest() {
        try {
            userModule.clearUser();
            await userModule.removeToken();

            return true;
        } catch {
            return false;
        }
    }
}

export default getModule(AuthModule);
